import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { TranslationService } from './translation.service';

/**
 * Localised labels for every `<mat-paginator>` in the app.
 *
 * Angular Material ships English defaults and nothing replaced them, so
 * "Items per page:" and "1 – 1 of 1" stayed English in all four locales — on the
 * 21 files that render a paginator, not only the provider domain.
 *
 * The one thing that makes this work: Material caches the label strings and only
 * re-reads them when `changes` emits. Setting the fields on a language change is
 * not enough — without `this.changes.next()` every paginator keeps showing the
 * previous language until something else forces a re-render.
 */
@Injectable()
export class ProporioPaginatorIntl extends MatPaginatorIntl implements OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly translation = inject(TranslationService);
  private readonly destroy$ = new Subject<void>();

  constructor() {
    super();
    this.applyLabels();
    this.translation.onLanguageChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyLabels());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Material calls this for the "1 – 10 of 42" range text. It is a method on the
   * instance rather than a string, so it is re-evaluated on every render and
   * needs no cache invalidation of its own.
   */
  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    if (length === 0 || pageSize === 0) {
      return this.translate.instant('shared.paginator.rangeEmpty', { length });
    }

    const total = Math.max(length, 0);
    const start = page * pageSize;
    // A page can run past the end when the last page is partial.
    const end = start < total ? Math.min(start + pageSize, total) : start + pageSize;

    return this.translate.instant('shared.paginator.range', {
      start: start + 1,
      end,
      length: total
    });
  };

  private applyLabels(): void {
    this.itemsPerPageLabel = this.translate.instant('shared.paginator.itemsPerPage');
    this.nextPageLabel = this.translate.instant('shared.paginator.nextPage');
    this.previousPageLabel = this.translate.instant('shared.paginator.previousPage');
    this.firstPageLabel = this.translate.instant('shared.paginator.firstPage');
    this.lastPageLabel = this.translate.instant('shared.paginator.lastPage');

    // Without this, the labels above are set but nothing re-reads them.
    this.changes.next();
  }
}
